import {
    Component,
    ChangeDetectionStrategy,
    Type,
    ViewChild,
    inject,
} from '@angular/core';
import {
    MatSelect,
    MatSelectChange,
    MatSelectModule,
} from '@angular/material/select';
import {
    FieldTypeConfig,
    FormlyFieldConfig,
    FormlyModule,
} from '@ngx-formly/core';
import {
    FieldType,
    FormlyFieldProps,
    FormlyMatFormFieldModule,
} from '@ngx-formly/material/form-field';
import {
    FormlyFieldSelectProps,
    FormlySelectModule,
} from '@ngx-formly/core/select';
import { ɵobserve as observe } from '@ngx-formly/core';
import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatPseudoCheckboxModule } from '@angular/material/core';
import { isObservable, Observable, of } from 'rxjs';
import { ElenchiService } from 'app/services/elenchi.service';
import { isArray } from 'lodash-es';
import { MultiSelectReadType } from '../multiselect-read.type';

interface SelectProps extends FormlyFieldProps, FormlyFieldSelectProps {
    multiple?: boolean;
    selectAllOption?: string;
    disableOptionCentering?: boolean;
    typeaheadDebounceInterval?: number;
    compareWith?: (o1: any, o2: any) => boolean;
    panelClass?: string;
    options_type?:string

}

export interface FormlySelectFieldConfig
    extends FormlyFieldConfig<SelectProps> {
    type: 'multiselect-edit' | Type<MultiSelectType>;
}


interface InputProps extends FormlyFieldProps {
    options_type:string
}

export interface FormlyInputFieldConfig extends FormlyFieldConfig<InputProps> {
    type: 'multiselect-read' | Type<MultiSelectReadType>;
}

@Component({
    selector: 'formly-field-mat-select',
    standalone: true,
    imports: [
        NgIf,
        NgFor,
        MatSelect,
        AsyncPipe,
        MatIconModule,
        FormlyModule,
        FormlyMatFormFieldModule,
        MatInputModule,
        FormlySelectModule,
        MatPseudoCheckboxModule,
        ReactiveFormsModule,
        MatSelectModule,
        MatFormFieldModule,
    ],
    template: `
      @if (formState.editMode) {

    <ng-template #selectAll let-selectOptions="selectOptions">
      <mat-option (click)="toggleSelectAll(selectOptions)">
        <mat-pseudo-checkbox class="mat-option-pseudo-checkbox" [state]="getSelectAllState(selectOptions)">
        </mat-pseudo-checkbox>
        {{ props.selectAllOption }}
      </mat-option>
    </ng-template>

    <mat-select
      [id]="id"
      [formControl]="formControl"
      [formlyAttributes]="field"
      [placeholder]="props.placeholder"
      [tabIndex]="props.tabindex"
      [required]="required"
      [compareWith]="props.compareWith"
      [multiple]="props.multiple"
      (selectionChange)="change($event)"
      [errorStateMatcher]="errorStateMatcher"
      [aria-label]="_getAriaLabel()"
      [aria-labelledby]="_getAriaLabelledby()"
      [disableOptionCentering]="props.disableOptionCentering"
      [typeaheadDebounceInterval]="props.typeaheadDebounceInterval"
      [panelClass]="props.panelClass"
    >
        <mat-select-trigger >

            <ng-container *ngFor="let item of formControl.value; let i = index">

            <p class="font-semibold mt-2" *ngIf="i === 0 || optionsLabel[item]?.group !== optionsLabel[formControl.value[i - 1]]?.group"> {{optionsLabel[item]?.group}}</p>

               <p class="mt-0.5"> {{optionsLabel[item]?.label}}</p>

            </ng-container>
        </mat-select-trigger>

      <ng-container *ngIf="props.options | formlySelectOptions : field | async as selectOptions">
        <ng-container
          *ngIf="props.multiple && props.selectAllOption"
          [ngTemplateOutlet]="selectAll"
          [ngTemplateOutletContext]="{ selectOptions: selectOptions }"
        >
        </ng-container>
        <ng-container *ngFor="let item of selectOptions">
          <mat-optgroup *ngIf="item.group" [label]="item.label">
          <mat-option *ngFor="let child of item.group" [value]="child.value" [disabled]="child.disabled">
            {{ child.label }}
          </mat-option>
        </mat-optgroup>
        <mat-option *ngIf="!item.group" [value]="item.value" [disabled]="item.disabled">{{ item.label }}</mat-option>
        </ng-container>
      </ng-container>
    </mat-select>
      }
      @else{
        <div *ngIf="(labels$ | async) as values ">
    <ng-container *ngFor="let item of values; let i = index">
      <ng-container *ngIf="item?.group || item?.label">
        <p class="font-semibold" *ngIf="i === 0 || item.group !== values[i - 1].group">{{item.group}}</p>
        <p class="mt-0.5">{{item.label}}</p>
      </ng-container>
   </ng-container>
  </div>

      }`,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiSelectType extends FieldType<FieldTypeConfig<SelectProps>> {
    optionsLabel: any = {};

    ngOnInit() {
        //DA VEDERE !!!!!!

        if (isObservable(this.props.options)) {
            const options = this.props.options as Observable<any>;
            options.subscribe((elenco) =>
                elenco.forEach((item) => {
                    this.optionsLabel[item['value']] = item;
                }),
            );
        } else {
            this.props.options.forEach((item) => {
                this.optionsLabel[item['value']] = item;
            });
        }

        const values = this.formControl.value
    
        if (isObservable(this.props.options)){
            this.labels$ = this._elenchiService.getLabelsOption(this.field.props.options_type, values)
        }
        else if (isArray(this.props.options)){
            const res = this.props.options.filter((x: any) =>values?.indexOf(x.value)!=-1);
            this.labels$=of(res)
        }
        else{
            return of(values)
        }
    }

    @ViewChild(MatSelect, { static: false }) set select(select: any) {
        if (!select) return
        observe(
            select,
            ['_parentFormField', '_textField'],
            ({ currentValue }) => {
                if (currentValue) {
                    select._preferredOverlayOrigin =
                        select._parentFormField.getConnectedOverlayOrigin();
                }
            },
        );
    }
    override defaultOptions = {
        props: {
            compareWith(o1: any, o2: any) {
                return o1 === o2;
            },
        },
    };

    private selectAllValue!: { options: any; value: any[] };

    getSelectAllState(options: any[]) {
        if (this.empty || this.value.length === 0) {
            return null;
        }

        return this.value.length !== this.getSelectAllValue(options).length
            ? 'indeterminate'
            : 'checked';
    }

    toggleSelectAll(options: any[]) {
        const selectAllValue = this.getSelectAllValue(options);
        this.formControl.setValue(
            !this.value || this.value.length !== selectAllValue.length
                ? selectAllValue
                : [],
        );
        this.formControl.markAsDirty();
    }

    change($event: MatSelectChange) {
        this.props.change?.(this.field, $event);
    }

    _getAriaLabelledby() {
        if (this.props.attributes?.['aria-labelledby']) {
            return this.props.attributes['aria-labelledby'] as string;
        }

        return this.formField?._labelId;
    }

    _getAriaLabel() {
        return this.props.attributes?.['aria-label'] as string;
    }

    private getSelectAllValue(options: any[]) {
        if (!this.selectAllValue || options !== this.selectAllValue.options) {
            const flatOptions: any[] = [];
            options.forEach((o) =>
                o.group ? flatOptions.push(...o.group) : flatOptions.push(o),
            );

            this.selectAllValue = {
                options,
                value: flatOptions
                    .filter((o) => !o.disabled)
                    .map((o) => o.value),
            };
        }

        return this.selectAllValue.value;
    }


    values:any[];
      labels$:Observable<string[]>
      
      private _elenchiService = inject(ElenchiService)
    
    
        getOptionValues(values:string[]): string[]{
            const value = this.formControl.value
            const options = this.props.options as Array<any>
            const result = [];
    
            if (options){
    
                if (values instanceof Array){
                    for (let index in values){
                        const vv = options.filter((x: any) => String(x.value) == String(values[index]))
                        result.push(vv[0])
                    }
                }
                else{
                    const vv = options.filter((x: any) => String(x.value) == String(values))
                    result.push(vv[0])
                }
                return result
    
            }
            else{
    
                return [value]
            }
    
        }
    
        getValues(): string[] {
    
            const value = this.formControl.value
            const options = this.props.options as Array<any>
    
            if (value instanceof(Array)){
                return this.getOptionValues(value)
            }
            else{
                return this.getOptionValues([value])
            }
    
            if (options) {
              return options.filter((x: any) => String(x.value) == String(value))[0]?.label
            }
            else {
    
              return value
            }
          }
    
}
